
import React, { useEffect, useState } from "react";

import {
    Add,
    Delete,
    Edit,
    Search,
    Refresh,
    AccountTree,
    Save,
    Cancel,
} from "@mui/icons-material";

import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import {
    getAllocations,
    insertAllocation,
    updateAllocation,
    deleteAllocation,
} from "../../../services/allocationService";

import { getAllocationHeads } from "../../../services/allocationHeadService";

const Allocation = () => {
    const [rows, setRows] = useState([]);
    const [allocationHeads, setAllocationHeads] = useState([]);

    const [loading, setLoading] = useState(false);

    const [editMode, setEditMode] = useState(false);

    const [open, setOpen] = useState(false);

    const [search, setSearch] = useState("");

    const [form, setForm] = useState({
        AllocationHeadCode: "",
        Allocation: "",
        OldAllocation: "",
    });

    // ========================================================
    // LOAD DATA
    // ========================================================

    const loadData = async () => {
        try {
            setLoading(true);

            const res = await getAllocations();

            setRows(res.data || []);
        } catch (error) {
            console.error(
                "Load allocation error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to load allocations"
            );
        } finally {
            setLoading(false);
        }
    };

    // ========================================================
    // LOAD ALLOCATION HEADS
    // ========================================================

    const loadAllocationHeads = async () => {
        try {
            const res = await getAllocationHeads();

            setAllocationHeads(res.data || []);
        } catch (error) {
            console.error(
                "Load allocation heads error:",
                error
            );
        }
    };

    useEffect(() => {
        loadData();
        loadAllocationHeads();
    }, []);

    // ========================================================
    // REFRESH
    // ========================================================

    const handleRefresh = async () => {
        setSearch("");
        await loadData();
    };

    // ========================================================
    // FORM CHANGE
    // ========================================================

    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // ========================================================
    // ALLOCATION HEAD MAP
    // ========================================================

    const allocationHeadMap = Object.fromEntries(
        allocationHeads.map((head) => [
            String(head.AllocationHeadCode),
            head.AllocationHead,
        ])
    );

    // ========================================================
    // OPEN NEW
    // ========================================================

    const handleAdd = () => {
        setEditMode(false);

        setForm({
            AllocationHeadCode: "",
            Allocation: "",
            OldAllocation: "",
        });

        setOpen(true);
    };

    // ========================================================
    // EDIT
    // ========================================================

    const handleEdit = (row) => {
        setEditMode(true);

        setForm({
            AllocationHeadCode:
                row.AllocationHeadCode ?? "",

            OldAllocation:
                row.Allocation ?? "",

            Allocation:
                row.Allocation ?? "",
        });

        setOpen(true);
    };

    // ========================================================
    // CLOSE DIALOG
    // ========================================================

    const handleClose = () => {
        if (loading) return;

        setOpen(false);

        setEditMode(false);

        setForm({
            AllocationHeadCode: "",
            Allocation: "",
            OldAllocation: "",
        });
    };

    // ========================================================
    // SAVE / UPDATE
    // ========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (!form.AllocationHeadCode) {
                alert(
                    "Please select Allocation Head"
                );
                return;
            }

            if (
                form.Allocation === "" ||
                form.Allocation === null ||
                form.Allocation === undefined
            ) {
                alert(
                    "Please enter Allocation"
                );
                return;
            }

            setLoading(true);

            if (editMode) {
                await updateAllocation(
                    form.OldAllocation,
                    {
                        NewAllocation:
                            form.Allocation,
                    }
                );

                alert(
                    "Allocation updated successfully"
                );
            } else {
                await insertAllocation(form);

                alert(
                    "Allocation saved successfully"
                );
            }

            setOpen(false);
            setEditMode(false);

            setForm({
                AllocationHeadCode: "",
                Allocation: "",
                OldAllocation: "",
            });

            await loadData();
        } catch (error) {
            console.error(
                "Save allocation error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to save allocation"
            );
        } finally {
            setLoading(false);
        }
    };

    // ========================================================
    // DELETE
    // ========================================================

    const handleDelete = async (Allocation) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete this allocation?\n\nAllocation: ${Allocation}`
        );

        if (!confirmDelete) return;

        try {
            setLoading(true);

            await deleteAllocation(Allocation);

            alert(
                "Allocation deleted successfully"
            );

            await loadData();
        } catch (error) {
            console.error(
                "Delete allocation error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to delete allocation"
            );
        } finally {
            setLoading(false);
        }
    };

    // ========================================================
    // SEARCH
    // ========================================================

    const filteredRows = rows.filter((row) => {
        const searchText =
            search.trim().toLowerCase();

        if (!searchText) return true;

        const searchableText = [
            row.AllocationHeadCode,
            allocationHeadMap[
                String(row.AllocationHeadCode)
            ],
            row.Allocation,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return searchableText.includes(
            searchText
        );
    });

    // ========================================================
    // UI
    // ========================================================

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#f5f7fa",
                p: {
                    xs: 1.5,
                    sm: 2,
                    md: 3,
                },
                boxSizing: "border-box",
            }}
        >
            {/* =================================================
                PROFESSIONAL HEADER
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    p: {
                        xs: 2.5,
                        md: 3.5,
                    },
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    background:
                        "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Decorative Background */}

                <Box
                    sx={{
                        position: "absolute",
                        right: -50,
                        top: -60,
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        background:
                            "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
                    }}
                />

                <Stack
                    direction={{
                        xs: "column",
                        sm: "row",
                    }}
                    spacing={2.5}
                    alignItems={{
                        xs: "flex-start",
                        sm: "center",
                    }}
                    sx={{
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    {/* Icon */}

                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "linear-gradient(135deg, #1976d2, #42a5f5)",
                            color: "white",
                            boxShadow:
                                "0 8px 20px rgba(25,118,210,0.25)",
                            flexShrink: 0,
                        }}
                    >
                        <AccountTree
                            sx={{
                                fontSize: 34,
                            }}
                        />
                    </Box>

                    {/* Title */}

                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            sx={{
                                fontSize: {
                                    xs: "1.65rem",
                                    md: "2rem",
                                },
                            }}
                        >
                            Allocation Entry
                        </Typography>

                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                maxWidth: 700,
                            }}
                        >
                            Manage allocation codes and
                            their associated allocation heads.
                        </Typography>
                    </Box>

                    {/* Record Count */}

                    <Box
                        sx={{
                            minWidth: {
                                xs: "100%",
                                sm: 180,
                            },
                            textAlign: {
                                xs: "left",
                                sm: "right",
                            },
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                            sx={{
                                textTransform:
                                    "uppercase",
                                letterSpacing: 0.8,
                            }}
                        >
                            Total Allocations
                        </Typography>

                        <Typography
                            variant="h5"
                            color="primary"
                            fontWeight={800}
                            sx={{
                                mt: 0.3,
                            }}
                        >
                            {rows.length.toLocaleString()}
                        </Typography>

                        <Chip
                            label={
                                loading
                                    ? "Loading..."
                                    : rows.length
                                        ? "Data Available"
                                        : "No Records"
                            }
                            size="small"
                            color={
                                loading
                                    ? "warning"
                                    : rows.length
                                        ? "success"
                                        : "default"
                            }
                            variant="outlined"
                            sx={{
                                mt: 0.5,
                                fontWeight: 600,
                            }}
                        />
                    </Box>
                </Stack>
            </Paper>

            {/* =================================================
                ACTION / SEARCH CARD
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        px: {
                            xs: 2,
                            md: 3,
                        },
                        py: 2,
                        backgroundColor: "grey.50",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={1.5}
                        alignItems={{
                            xs: "flex-start",
                            md: "center",
                        }}
                        justifyContent="space-between"
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <Search
                                color="primary"
                                sx={{
                                    fontSize: 27,
                                }}
                            />

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                >
                                    Find Allocation
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Search by allocation
                                    head or allocation code.
                                </Typography>
                            </Box>
                        </Stack>

                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAdd}
                            disabled={loading}
                            sx={{
                                minWidth: {
                                    xs: "100%",
                                    sm: 180,
                                },
                                height: 44,
                                borderRadius: 2,
                                fontWeight: 700,
                                textTransform: "none",
                            }}
                        >
                            Add Allocation
                        </Button>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        p: {
                            xs: 2,
                            md: 2.5,
                        },
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={1.5}
                        alignItems={{
                            xs: "stretch",
                            sm: "center",
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label="Search Allocations"
                            placeholder="Search allocation head or allocation code..."
                            value={search}
                            onChange={(e) =>
                                setSearch(
                                    e.target.value
                                )
                            }
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <Search
                                            sx={{
                                                mr: 1,
                                                color: "action.active",
                                            }}
                                        />
                                    ),
                                },
                            }}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={
                                handleRefresh
                            }
                            disabled={loading}
                            sx={{
                                minWidth: {
                                    xs: "100%",
                                    sm: 125,
                                },
                                height: 40,
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: "none",
                            }}
                        >
                            {loading
                                ? "Loading..."
                                : "Refresh"}
                        </Button>
                    </Stack>

                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={1}
                        sx={{
                            mt: 2,
                        }}
                    >
                        <Chip
                            label={`Found: ${filteredRows.length}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{
                                fontWeight: 600,
                            }}
                        />

                        {search && (
                            <Chip
                                label={`Search: "${search}"`}
                                color="default"
                                variant="outlined"
                                size="small"
                            />
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* =================================================
                TABLE
            ================================================= */}

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                }}
            >
                {/* Table Header */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            md: 3,
                        },
                        py: 2,
                        backgroundColor: "grey.50",
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={1}
                        alignItems={{
                            xs: "flex-start",
                            sm: "center",
                        }}
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                fontWeight={700}
                            >
                                Allocation Records
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Manage existing allocation
                                mappings.
                            </Typography>
                        </Box>

                        <Chip
                            icon={
                                <AccountTree />
                            }
                            label={`${filteredRows.length.toLocaleString()} Records`}
                            color="primary"
                            variant="outlined"
                            sx={{
                                fontWeight: 600,
                            }}
                        />
                    </Stack>
                </Box>

                <Divider />

                <TableContainer
                    sx={{
                        maxHeight: {
                            xs: "60vh",
                            sm: 600,
                        },
                        overflowX: "auto",
                    }}
                >
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            minWidth: 700,
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        backgroundColor:
                                            "#1976d2",
                                        color: "#ffffff",
                                        width: 90,
                                    }}
                                >
                                    S.No.
                                </TableCell>

                                <TableCell
                                    sx={{
                                        fontWeight: 700,
                                        backgroundColor:
                                            "#1976d2",
                                        color: "#ffffff",
                                    }}
                                >
                                    Allocation Head
                                </TableCell>

                                <TableCell
                                    align="right"
                                    sx={{
                                        fontWeight: 700,
                                        backgroundColor:
                                            "#1976d2",
                                        color: "#ffffff",
                                        width: 180,
                                    }}
                                >
                                    Allocation
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 700,
                                        backgroundColor:
                                            "#1976d2",
                                        color: "#ffffff",
                                        width: 150,
                                    }}
                                >
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        align="center"
                                    >
                                        <Typography
                                            sx={{
                                                py: 5,
                                            }}
                                            color="text.secondary"
                                        >
                                            Loading allocations...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredRows.map(
                                    (row, index) => (
                                        <TableRow
                                            key={`${row.AllocationHeadCode}-${row.Allocation}-${index}`}
                                            hover
                                            sx={{
                                                "&:nth-of-type(even)":
                                                    {
                                                        backgroundColor:
                                                            "#fafafa",
                                                    },
                                                "&:hover":
                                                    {
                                                        backgroundColor:
                                                            "rgba(25,118,210,0.05)",
                                                    },
                                            }}
                                        >
                                            <TableCell>
                                                {index +
                                                    1}
                                            </TableCell>

                                            <TableCell>
                                                <Stack
                                                    spacing={
                                                        0.25
                                                    }
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={
                                                            600
                                                        }
                                                    >
                                                        {
                                                            allocationHeadMap[
                                                                String(
                                                                    row.AllocationHeadCode
                                                                )
                                                            ] ||
                                                            "Unknown Head"
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Code:{" "}
                                                        {
                                                            row.AllocationHeadCode
                                                        }
                                                    </Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                            >
                                                <Chip
                                                    label={
                                                        row.Allocation
                                                    }
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 700,
                                                        minWidth: 80,
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={
                                                        0.5
                                                    }
                                                    justifyContent="center"
                                                >
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() =>
                                                            handleEdit(
                                                                row
                                                            )
                                                        }
                                                        disabled={
                                                            loading
                                                        }
                                                        size="small"
                                                        title="Edit Allocation"
                                                    >
                                                        <Edit />
                                                    </IconButton>

                                                    <IconButton
                                                        color="error"
                                                        onClick={() =>
                                                            handleDelete(
                                                                row.Allocation
                                                            )
                                                        }
                                                        disabled={
                                                            loading
                                                        }
                                                        size="small"
                                                        title="Delete Allocation"
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    )
                                )
                            )}

                            {!loading &&
                                filteredRows.length ===
                                0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                        >
                                            <Box
                                                sx={{
                                                    py: 6,
                                                }}
                                            >
                                                <AccountTree
                                                    sx={{
                                                        fontSize: 45,
                                                        color: "text.disabled",
                                                        mb: 1,
                                                    }}
                                                />

                                                <Typography
                                                    variant="body1"
                                                    fontWeight={
                                                        600
                                                    }
                                                >
                                                    No allocations
                                                    found
                                                </Typography>

                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    {search
                                                        ? "Try a different search term."
                                                        : "Add your first allocation using the button above."}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Footer */}

                <Box
                    sx={{
                        px: {
                            xs: 2,
                            md: 3,
                        },
                        py: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "grey.50",
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Showing filtered records
                        </Typography>

                        <Typography
                            variant="body2"
                            fontWeight={700}
                            color="primary"
                        >
                            {filteredRows.length.toLocaleString()}
                        </Typography>
                    </Stack>
                </Box>
            </Paper>

            {/* =================================================
                ADD / UPDATE DIALOG
            ================================================= */}

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                    },
                }}
            >
                {/* Dialog Header */}

                <DialogTitle
                    sx={{
                        p: 0,
                    }}
                >
                    <Box
                        sx={{
                            px: 3,
                            py: 2.5,
                            background:
                                "linear-gradient(135deg, #1976d2, #42a5f5)",
                            color: "#ffffff",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    backgroundColor:
                                        "rgba(255,255,255,0.18)",
                                }}
                            >
                                {editMode ? (
                                    <Edit />
                                ) : (
                                    <Add />
                                )}
                            </Box>

                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                >
                                    {editMode
                                        ? "Update Allocation"
                                        : "Add Allocation"}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        opacity:
                                            0.9,
                                        mt: 0.25,
                                    }}
                                >
                                    {editMode
                                        ? "Modify the allocation code."
                                        : "Create a new allocation mapping."}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </DialogTitle>

                <form
                    onSubmit={
                        handleSubmit
                    }
                >
                    <DialogContent
                        sx={{
                            p: {
                                xs: 2,
                                sm: 3,
                            },
                        }}
                    >
                        <Stack
                            spacing={2.5}
                            sx={{
                                mt: 1,
                            }}
                        >
                            {/* Allocation Head */}

                            <TextField
                                select
                                fullWidth
                                label="Allocation Head"
                                name="AllocationHeadCode"
                                value={
                                    form.AllocationHeadCode
                                }
                                onChange={
                                    handleChange
                                }
                                disabled={
                                    editMode ||
                                    loading
                                }
                                required
                                helperText={
                                    editMode
                                        ? "Allocation Head cannot be changed while updating."
                                        : "Select the allocation head."
                                }
                            >
                                {allocationHeads.map(
                                    (head) => (
                                        <MenuItem
                                            key={
                                                head.AllocationHeadCode
                                            }
                                            value={
                                                head.AllocationHeadCode
                                            }
                                        >
                                            {
                                                head.AllocationHead
                                            }
                                        </MenuItem>
                                    )
                                )}
                            </TextField>

                            {/* Allocation */}

                            <TextField
                                fullWidth
                                label="Allocation"
                                name="Allocation"
                                type="number"
                                value={
                                    form.Allocation
                                }
                                onChange={
                                    handleChange
                                }
                                required
                                disabled={
                                    loading
                                }
                                inputProps={{
                                    step: "0.01",
                                }}
                                helperText={
                                    editMode
                                        ? `Current allocation: ${form.OldAllocation}`
                                        : "Enter the allocation code/value."
                                }
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            px: {
                                xs: 2,
                                sm: 3,
                            },
                            pb: 2.5,
                            gap: 1,
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={
                                <Cancel />
                            }
                            onClick={
                                handleClose
                            }
                            disabled={
                                loading
                            }
                            sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform:
                                    "none",
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            color={
                                editMode
                                    ? "warning"
                                    : "primary"
                            }
                            startIcon={
                                editMode ? (
                                    <Edit />
                                ) : (
                                    <Save />
                                )
                            }
                            disabled={
                                loading
                            }
                            sx={{
                                minWidth: 120,
                                borderRadius: 2,
                                fontWeight: 700,
                                textTransform:
                                    "none",
                            }}
                        >
                            {loading
                                ? "Please wait..."
                                : editMode
                                    ? "Update"
                                    : "Save"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Allocation;

